import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { loginStart, loginSuccess, loginFailure } from "../app/authSlice";
import { useLoginMutation } from "../app/api/console/consoleAuthApiSlice";
import LogoWordmark from "../assets/zenyt_logo_wordmark.svg";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error, isAuthenticated } = useSelector((state) => state.auth);
  const [login, { isLoading: isApiLoading }] = useLoginMutation();

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(loginStart());

    try {
      const userData = await login({ email, password }).unwrap();
      if (userData.access_token) {
        localStorage.setItem("token", userData.access_token);
      }
      dispatch(loginSuccess(userData));
      localStorage.setItem("isAuthenticated", "true");
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    } catch (err) {
      dispatch(loginFailure(err.data?.detail || "Authentication failed"));
    }
  };

  return (
    <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-sidebar-gradient-from to-sidebar-gradient-to font-sans'>
      <div className='w-full max-w-sm'>
        <div className='bg-white rounded-2xl shadow-xl p-8'>
          <div className='flex flex-col items-center mb-8'>
            <img src={LogoWordmark} alt='Zenyt' className='h-7 mb-3' />
            <span className='text-text-tertiary text-body-sm'>Sign in to Console</span>
          </div>

          <form onSubmit={handleSubmit} className='space-y-4'>
            <div>
              <label htmlFor='email' className='block text-body-xs font-medium text-text-secondary mb-1.5'>
                Email
              </label>
              <input
                id='email'
                name='email'
                type='email'
                autoComplete='email'
                required
                className='w-full px-3.5 py-2.5 text-body-sm text-text-primary bg-surface-neutral rounded-xl border border-border-subtle focus:outline-none focus:ring-2 focus:ring-accent-purple/30 focus:border-accent-purple/50 placeholder:text-text-tertiary transition-all'
                placeholder='you@company.com'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <div className='flex items-center justify-between mb-1.5'>
                <label htmlFor='password' className='block text-body-xs font-medium text-text-secondary'>
                  Password
                </label>
                <Link to='/forgot-password' className='text-body-xs text-accent-purple hover:underline'>
                  Forgot password?
                </Link>
              </div>
              <input
                id='password'
                name='password'
                type='password'
                autoComplete='current-password'
                required
                className='w-full px-3.5 py-2.5 text-body-sm text-text-primary bg-surface-neutral rounded-xl border border-border-subtle focus:outline-none focus:ring-2 focus:ring-accent-purple/30 focus:border-accent-purple/50 placeholder:text-text-tertiary transition-all'
                placeholder='Password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {location.state?.notice && !error && (
              <div className='px-3 py-2 text-body-xs text-text-secondary bg-surface-neutral rounded-lg text-center'>
                {location.state.notice}
              </div>
            )}

            {error && (
              <div className='px-3 py-2 text-body-xs text-severity-high bg-severity-high/5 rounded-lg text-center'>
                {error}
              </div>
            )}

            <button
              type='submit'
              disabled={isLoading || isApiLoading}
              className='w-full py-2.5 text-body-sm font-semibold text-white bg-button-primary rounded-xl hover:bg-button-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2'>
              {isLoading || isApiLoading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
